import React, { useEffect, useRef, useState } from 'react';
import DeluxePaymentTabSkeleton from './payment-method-tabs/DeluxePaymentTabSkeleton';
import { formatWalletError, isWalletCancel } from './isWalletCancel';
import './payment-method-tabs/deluxe-booking-payment-tabs.css';
import './DeluxeWalletButtons.css';

const SCRIPT_ID = 'deluxe-embedded-payments-sdk';

function readDeluxeSdk() {
  try {
    return new Function(
      'return typeof EmbeddedPayments !== "undefined" ? EmbeddedPayments : (typeof DigitalWalletsPay !== "undefined" ? DigitalWalletsPay : null)'
    )();
  } catch {
    return null;
  }
}

function loadEmbeddedScript(scriptUrl) {
  return new Promise((resolve, reject) => {
    const ready = readDeluxeSdk();
    if (ready?.init) {
      resolve(ready);
      return;
    }
    const finish = () => {
      const sdk = readDeluxeSdk();
      if (sdk?.init) resolve(sdk);
      else reject(new Error('Deluxe wallets are unavailable'));
    };
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      if (existing.dataset.loaded === 'true') {
        finish();
        return;
      }
      existing.addEventListener('load', finish, { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load Deluxe wallets')), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = scriptUrl;
    script.async = true;
    script.onload = () => {
      script.dataset.loaded = 'true';
      finish();
    };
    script.onerror = () => reject(new Error('Failed to load Deluxe wallets'));
    document.head.appendChild(script);
  });
}

function asRecord(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function cleanDeluxeEmbedIframe(instance, scriptUrl) {
  const iframe = document.getElementById('payment-form');
  if (!iframe?.src) return;
  const raw = iframe.src.replace(/[\r\n\t]/g, '').replace(/ +/g, '');
  const url = new URL(raw, window.location.href);
  const origin = new URL(instance.baseUrl || scriptUrl, window.location.href).origin;
  const clean = new URL('/embedded/embedded/render', origin);
  url.searchParams.forEach((value, key) => {
    clean.searchParams.set(key, value.trim());
  });
  iframe.src = clean.toString();
}

function hideDeluxeProductPanels(container) {
  if (!container) return;
  container.querySelectorAll(
    '#prod-panel, #product-panel, #productPanel, .prod-panel, .product-panel, [id*="prod-panel"], [class*="prod-panel"], [class*="ProdPanel"]'
  ).forEach((el) => {
    if (el.tagName === 'IFRAME') return;
    el.setAttribute('hidden', 'true');
    el.style.display = 'none';
  });
}

function swallowGooglePayCancels() {
  const PaymentsClient = window.google?.payments?.api?.PaymentsClient;
  const proto = PaymentsClient?.prototype;
  if (!proto?.loadPaymentData || proto.loadPaymentData.__neatWrapped) return;
  const original = proto.loadPaymentData;
  proto.loadPaymentData = function loadPaymentDataWrapped(...args) {
    return Promise.resolve(original.apply(this, args)).catch((reason) => {
      if (isWalletCancel(reason)) return undefined;
      throw reason;
    });
  };
  proto.loadPaymentData.__neatWrapped = true;
}

function extractPaymentId(data) {
  const payload = asRecord(data?.data || data);
  return String(
    payload.paymentId ||
      payload.PaymentId ||
      payload.transactionId ||
      payload.TransactionId ||
      payload.id ||
      data?.paymentId ||
      ''
  ).trim();
}

const DeluxeWalletButtons = ({
  jwt,
  scriptUrl,
  method = 'googlePay',
  disabled = false,
  onSuccess,
  onError,
}) => {
  const containerIdRef = useRef(`deluxe-wallets-${Math.random().toString(36).slice(2, 10)}`);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [hidden, setHidden] = useState(false);
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;

  useEffect(() => {
    let cancelled = false;
    if (!jwt || !scriptUrl || disabled) return undefined;

    setReady(false);
    setError('');
    setHidden(false);
    swallowGooglePayCancels();

    const onUnhandledRejection = (event) => {
      if (!isWalletCancel(event.reason)) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
    };
    window.addEventListener('unhandledrejection', onUnhandledRejection, true);
    const googlePayWatch = window.setInterval(swallowGooglePayCancels, 500);

    loadEmbeddedScript(scriptUrl)
      .then((EmbeddedPayments) => {
        if (cancelled || !EmbeddedPayments) {
          throw new Error('Deluxe wallets are unavailable');
        }
        const googlePayEnv = /payments2\.deluxe\.com/i.test(scriptUrl) ? 'TEST' : 'PRODUCTION';
        return EmbeddedPayments.init(jwt, {
          countryCode: 'US',
          currencyCode: 'USD',
          paymentMethods: [method === 'applePay' ? 'applePay' : 'googlePay'],
          merchantCapabilities: ['supports3DS'],
          allowedCardAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
          supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
          googlePayEnv,
          showApplePayButton: method === 'applePay' ? 'supported' : undefined,
        }).then((instance) => {
          if (cancelled) return instance;
          instance.renderProdPanel = () => {};
          const embedIframe = instance.embedIframe?.bind(instance);
          if (embedIframe) {
            instance.embedIframe = () => {
              embedIframe();
              cleanDeluxeEmbedIframe(instance, scriptUrl);
              hideDeluxeProductPanels(document.getElementById(containerIdRef.current));
            };
          }
          instance.setEventHandlers({
            onTxnSuccess: (_gateway, data) => {
              if (cancelled) return;
              const paymentId = extractPaymentId(data);
              if (!paymentId) {
                const message = 'Deluxe did not return a wallet payment id';
                setError(message);
                onErrorRef.current?.(new Error(message));
                return;
              }
              onSuccessRef.current?.({ paymentId });
            },
            onTxnFailed: (_gateway, data) => {
              if (isWalletCancel(data)) return;
              const message = formatWalletError(data) || 'Wallet payment failed';
              setError(message);
              onErrorRef.current?.(new Error(message));
            },
            onValidationError: (_gateway, errors) => {
              if (isWalletCancel(errors)) return;
              const message = Array.isArray(errors) ? errors.join(' ') : formatWalletError(errors) || 'Wallet payment could not start';
              setError(message);
            },
            onCancel: () => {},
          });
          const rendered = instance.render({
            containerId: containerIdRef.current,
            paymentpanelstyle: 'light',
          });
          hideDeluxeProductPanels(document.getElementById(containerIdRef.current));
          swallowGooglePayCancels();
          Promise.resolve(rendered).catch((reason) => {
            if (cancelled || isWalletCancel(reason)) return;
            const message = formatWalletError(reason);
            if (message) {
              setError(message);
              onErrorRef.current?.(new Error(message));
            }
          });
          return undefined;
        });
      })
      .then(() => {
        if (!cancelled) {
          hideDeluxeProductPanels(document.getElementById(containerIdRef.current));
          swallowGooglePayCancels();
          setReady(true);
        }
      })
      .catch((reason) => {
        if (cancelled || isWalletCancel(reason)) return;
        setHidden(true);
      });

    return () => {
      cancelled = true;
      window.clearInterval(googlePayWatch);
      window.removeEventListener('unhandledrejection', onUnhandledRejection, true);
    };
  }, [jwt, scriptUrl, method, disabled]);

  if (hidden) return null;

  return (
    <div className="deluxe-wallets" aria-busy={!ready && !error}>
      {error && (
        <div className="deluxe-wallets__error" role="alert">
          {error}
        </div>
      )}
      <div className="deluxe-wallets__stage">
        {!ready && !error ? <DeluxePaymentTabSkeleton variant="wallet" /> : null}
        <div
          id={containerIdRef.current}
          className={`deluxe-wallets__frame ${ready ? '' : 'deluxe-wallets__frame--pending'}`}
        />
      </div>
    </div>
  );
};

export default DeluxeWalletButtons;
